import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TimezoneCombobox } from "@/components/ui/timezone-combobox";
import { asEventId } from "@/types/identifiers";
import { defaultSelectedBoatIds } from "@/domain/event-boats";
import type { AdminEventsActions } from "@/hooks/use-admin-events-actions";
import type { components } from "@/api/generated/schema";

type BoatWithTeam = components["schemas"]["BoatWithTeam"];
type EventResponse = components["schemas"]["EventResponse"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The event being edited, or null to create a new one. */
  event: EventResponse | null;
  /** All boats (one per team) — the create form fans out a build per selected boat. */
  boats: BoatWithTeam[];
  /** Pre-filled season for a new event (latest known season). */
  defaultSeason: number;
  /** Existing events, for a soft duplicate (name + season) warning on create. */
  existingEvents: EventResponse[];
  actions: AdminEventsActions;
  /** Called with the new event id after a successful create, for auto-expand. */
  onCreated: (eventId: string) => void;
}

export function EventFormDialog(props: Props) {
  const { open, onOpenChange, event } = props;
  // The timezone combobox portals its popup here so it stays inside the modal's
  // interactive layer (Radix sets pointer-events:none outside the dialog). A
  // callback-ref-to-state makes the container identity change null→element,
  // which re-triggers base-ui's portal resolution once the dialog has mounted.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={setContainer}>
        {open && (
          <EventForm
            key={event?.id ?? "new"}
            {...props}
            container={container}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function teamLabel(boat: BoatWithTeam): string {
  return boat.team?.name ?? boat.boat_code;
}

function EventForm({
  event,
  boats,
  defaultSeason,
  existingEvents,
  actions,
  onCreated,
  container,
  onDone,
}: Props & { onDone: () => void; container?: HTMLElement | null }) {
  const isEdit = event !== null;

  const [name, setName] = useState(event?.name ?? "");
  const [season, setSeason] = useState(String(event?.season ?? defaultSeason));
  const [startDate, setStartDate] = useState(event?.start_date ?? "");
  const [endDate, setEndDate] = useState(event?.end_date ?? "");
  const [location, setLocation] = useState(event?.location ?? "");
  const [code, setCode] = useState(event?.code ?? "");
  const [timezone, setTimezone] = useState<string | null>(
    event?.timezone ??
      (isEdit ? null : Intl.DateTimeFormat().resolvedOptions().timeZone),
  );

  const sortedBoats = useMemo(
    () => [...boats].sort((a, b) => teamLabel(a).localeCompare(teamLabel(b))),
    [boats],
  );
  // Create fans out a build per selected boat; default to every team boat. The
  // shared hull (B15) is listed but stays unticked until an event needs it.
  const [selectedBoats, setSelectedBoats] = useState<Set<string>>(() =>
    defaultSelectedBoatIds(boats),
  );

  const seasonNum = Number(season);
  const seasonValid = Number.isInteger(seasonNum) && seasonNum > 0;
  const datesValid = !endDate || !startDate || endDate >= startDate;
  const canSave =
    name.trim() !== "" &&
    seasonValid &&
    startDate !== "" &&
    location.trim() !== "" &&
    datesValid &&
    !actions.isSaving;

  const duplicate =
    !isEdit &&
    name.trim() !== "" &&
    seasonValid &&
    existingEvents.some(
      (e) =>
        e.name.trim().toLowerCase() === name.trim().toLowerCase() &&
        e.season === seasonNum,
    );

  function toggleBoat(id: string) {
    setSelectedBoats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allSelected = selectedBoats.size === boats.length;

  async function save() {
    const payload = {
      name: name.trim(),
      season: seasonNum,
      start_date: startDate,
      location: location.trim(),
      // Blank clears it server-side; the API upper-cases whatever is sent.
      code: code.trim() || null,
      end_date: endDate || null,
      timezone: timezone || null,
    };
    try {
      if (isEdit && event) {
        await actions.handleUpdateEvent(asEventId(event.id), payload);
        onDone();
      } else {
        const created = await actions.handleCreateEvent(
          payload,
          [...selectedBoats],
        );
        onCreated(created.id);
        onDone();
      }
    } catch {
      // Error toasted by the action; keep the dialog open for retry.
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit event" : "New event"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Update the event's details."
            : "Builds are generated for the selected teams."}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <Label htmlFor="event-name">Name</Label>
          <Input
            id="event-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., S6-DUB"
          />
          {duplicate && (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              An event named “{name.trim()}” already exists in season {seasonNum}.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="event-season">Season</Label>
            <Input
              id="event-season"
              type="number"
              min={1}
              value={season}
              onChange={(e) => setSeason(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event-location">Location</Label>
            <Input
              id="event-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Dubai, UAE"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="event-code">Venue code</Label>
          <Input
            id="event-code"
            value={code}
            maxLength={8}
            className="uppercase"
            onChange={(e) => setCode(e.target.value)}
            placeholder="POR"
          />
          <p className="text-xs text-muted-foreground">
            Shown with the season on weight reports, e.g. “POR S{seasonValid ? seasonNum : 6}”.
            Leave blank to fall back to the month.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="event-start">Start date</Label>
            <Input
              id="event-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event-end">End date</Label>
            <Input
              id="event-end"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Defaults to the start date.
            </p>
          </div>
        </div>
        {!datesValid && (
          <p className="text-xs text-red-600 dark:text-red-400">
            End date can't be before the start date.
          </p>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="event-tz">Timezone</Label>
          <TimezoneCombobox
            id="event-tz"
            value={timezone}
            onValueChange={setTimezone}
            container={container}
          />
          <p className="text-xs text-muted-foreground">
            Venue IANA zone — positions the “now” line on the schedule.
          </p>
        </div>

        {!isEdit && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Generate builds</Label>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() =>
                  setSelectedBoats(
                    allSelected ? new Set() : new Set(boats.map((b) => b.id)),
                  )
                }
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="max-h-44 overflow-y-auto rounded-md border divide-y">
              {sortedBoats.map((boat) => (
                <label
                  key={boat.id}
                  className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-accent/50"
                >
                  <input
                    type="checkbox"
                    className="accent-primary h-4 w-4"
                    checked={selectedBoats.has(boat.id)}
                    onChange={() => toggleBoat(boat.id)}
                  />
                  <span className="font-medium">{teamLabel(boat)}</span>
                  <span className="text-muted-foreground">{boat.boat_code}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedBoats.size} of {boats.length} teams
            </p>
          </div>
        )}
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onDone} disabled={actions.isSaving}>
          Cancel
        </Button>
        <Button onClick={save} disabled={!canSave}>
          {isEdit ? "Save" : "Create event"}
        </Button>
      </DialogFooter>
    </>
  );
}
